import { Box, Typography } from "@mui/material";
import { rem } from "polished";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ConfidentialityIntro,
  ConfidentialityPartnersOffersInformationOptIn,
  ConfidentialityPersonalDataOptIn,
  ConfidentialityPartnersOffersInformationOptOut,
  ConfidentialityPersonalDataOptOut,
  Modal,
  ModalFooter,
  ExternalLink,
} from "src/components";
import { EmeriaUrl } from "src/constants";
import { useAppForm, useMarketplaceLink } from "src/hooks";
import { useBrand } from "src/hooks/useBrand";
import z from "zod";

import {
  type ConfidentialityInputs,
  type ConsentFormProps,
} from "./consent-form.types";

export function EssetConsentForm({
  isOpen,
  isPending,
  onSubmit,
}: ConsentFormProps): React.ReactElement {
  const { t } = useTranslation();
  const { identity, customerPortal } = useBrand();
  const marketplaceLink = useMarketplaceLink();

  const defaultValues: ConfidentialityInputs = {
    personalDataOptIn: false,
    partnersOffersInformationOptIn: false,
    personalDataOptOut: false,
    partnersOffersInformationOptOut: false,
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: z.object({
        personalDataOptIn: z.boolean(),
        personalDataOptOut: z.boolean(),
        partnersOffersInformationOptIn: z.boolean(),
        partnersOffersInformationOptOut: z.boolean(),
      }),
    },
    onSubmit: ({ value }) => onSubmit(value),
  });

  const mutuallyExclusiveFieldValidator = (
    oppositeFieldName: keyof ConfidentialityInputs,
  ) => ({
    onChange: ({ value }: { value: boolean }) => {
      if (value && form.getFieldValue(oppositeFieldName)) {
        form.setFieldValue(oppositeFieldName, false);
      }
      return undefined;
    },
  });

  return (
    <Modal
      data-cy="mandatory-consent-modal"
      hideCloseButton
      disableBackdropClick
      disableEscapeKeyDown
      icon="confidentiality"
      title={t("Confidentialité")}
      open={isOpen}
      handleClose={form.handleSubmit}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Typography
          fontWeight="bold"
          variant="h3"
          sx={{
            fontSize: rem(14),
            margin: 0,
            lineHeight: 1.5,
            textAlign: "center",
          }}
        >
          {t("Bienvenue sur votre espace client {{portalName}}", {
            portalName: customerPortal.name,
          })}
        </Typography>

        <ConfidentialityIntro sx={{ mt: 2, textAlign: "center" }} />

        <Typography sx={{ my: 2, fontWeight: "bold" }}>
          {t("Pour {{brandName}} uniquement", { brandName: identity.name })}
        </Typography>

        <form.AppField
          name="personalDataOptIn"
          validators={mutuallyExclusiveFieldValidator("personalDataOptOut")}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPersonalDataOptIn />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <form.AppField
          name="personalDataOptOut"
          validators={mutuallyExclusiveFieldValidator("personalDataOptIn")}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPersonalDataOptOut />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <Typography sx={{ my: 2, fontWeight: "bold" }}>
          <Trans
            i18nKey="Pour les <subsidiaries>filiales Emeria</subsidiaries> et ses <partners>partenaires de confiance</partners>"
            components={{
              subsidiaries: (
                <ExternalLink href={EmeriaUrl}>{null}</ExternalLink>
              ),
              partners: marketplaceLink ? (
                <ExternalLink href={marketplaceLink}>{null}</ExternalLink>
              ) : (
                <span />
              ),
            }}
          />
        </Typography>

        <form.AppField
          name="partnersOffersInformationOptIn"
          validators={mutuallyExclusiveFieldValidator(
            "partnersOffersInformationOptOut",
          )}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPartnersOffersInformationOptIn />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <form.AppField
          name="partnersOffersInformationOptOut"
          validators={mutuallyExclusiveFieldValidator(
            "partnersOffersInformationOptIn",
          )}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPartnersOffersInformationOptOut />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <Typography sx={{ mt: 2, textAlign: "center" }}>
          {t(
            "À tout moment, vous pouvez ajuster vos préférences marketing au niveau de votre profil.",
          )}
        </Typography>

        <ModalFooter>
          <form.Subscribe selector={(state) => state.values}>
            {(values) => {
              const disabled =
                (!values.personalDataOptIn && !values.personalDataOptOut) ||
                (!values.partnersOffersInformationOptIn &&
                  !values.partnersOffersInformationOptOut);

              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <form.SubmitButton disabled={disabled} loading={isPending}>
                    {t("Envoyer")}
                  </form.SubmitButton>
                  {disabled && (
                    <Typography
                      variant="caption"
                      sx={{ color: "error.main", mt: 1, textAlign: "center" }}
                    >
                      {t(
                        "Pour pouvoir accéder à votre espace client, vous devez faire un choix : profiter/économiser ou renoncer !",
                      )}
                    </Typography>
                  )}
                </Box>
              );
            }}
          </form.Subscribe>
        </ModalFooter>
      </form>
    </Modal>
  );
}
